import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  styled,
  Switch,
  SwitchProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { CSS } from '@dnd-kit/utilities';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import { GetPFRCodeTableRow } from '../../../../services/apiTypes';
import { useDispatch, useSelector } from 'react-redux';
import {
  setAfterIndex,
  setCdCode,
  setReportedByCustomer,
  setRestoreSelectedR,
  setSelectedR,
} from '../../../../stores/confirmedInspection/slice';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../../stores/store';
import {
  TEXT_OVERFLOW_1LINE,
  TEXT_OVERFLOW_2LINES,
  TEXT_OVERFLOW_3LINES,
} from '../../../../_constants/styleConst';
import { isEmptyData } from '../../../../_utils/utils';

type AfterTableProps = {
  pfrCodeList: {
    before: GetPFRCodeTableRow[];
    after: GetPFRCodeTableRow[];
  };
  isConversionAssy: boolean;
  setIsConversionAssy: React.Dispatch<React.SetStateAction<boolean>>;
  beforeConversionAfter: GetPFRCodeTableRow[];
  setBeforeConversionAfter: React.Dispatch<
    React.SetStateAction<GetPFRCodeTableRow[]>
  >;
  isReported: boolean;
  setIsReported: React.Dispatch<React.SetStateAction<boolean>>;
};

const ConditionAssyRCode = {
  // 必須のRコード
  required: ['R014'],
  // 2つ以上選択必須のRコード
  multipleRequired: ['R012', 'R033', 'R125'],
  // ISA交換に包含されるRコード
  isa: [
    'R008',
    'R012',
    'R014',
    'R018',
    'R029',
    'R033',
    'R037',
    'R046',
    'R059',
    'R065',
    'R069',
    'R070',
    'R086',
    'R125',
    'R142',
    'R146',
    'R156',
    'R164',
    'R720',
  ],
};

/** iOS風のトグル */
const IOSSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 38,
  height: 18,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: '1px 2px',
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(19px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.primary.main,
        opacity: 1,
        border: 0,
        ...theme.applyStyles('dark', {
          backgroundColor: theme.palette.primary.main,
        }),
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: theme.palette.primary.main,
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[100],
      ...theme.applyStyles('dark', {
        color: theme.palette.grey[600],
      }),
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.7,
      ...theme.applyStyles('dark', {
        opacity: 0.3,
      }),
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 16,
    height: 16,
  },
  '& .MuiSwitch-track': {
    borderRadius: 22 / 2,
    backgroundColor: '#E9E9EA',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
    ...theme.applyStyles('dark', {
      backgroundColor: '#39393D',
    }),
  },
}));

const AfterTable: React.FC<AfterTableProps> = ({
  pfrCodeList,
  isConversionAssy,
  setIsConversionAssy,
  beforeConversionAfter,
  setBeforeConversionAfter,
  isReported,
  setIsReported,
}) => {
  const { t } = useTranslation();

  const { confirmedInspection } = useSelector(
    (state: RootState) => state.confirmedInspection,
  );

  const dispatch = useDispatch();
  const SortableItem = (props: any) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({
        id: props.id,
      });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <TableRow
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        sx={{
          backgroundColor: props.isExists ? 'inherit' : 'rgba(0,255,0,0.05)',
        }}
      >
        {props.children}
      </TableRow>
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = pfrCodeList.after.findIndex(
        (row) => row.index === active.id,
      );
      const newIndex = pfrCodeList.after.findIndex(
        (row) => row.index === over!.id,
      );

      // ここでドラッグ&ドロップでindexを変更した値を設定する
      const after = arrayMove(pfrCodeList.after, oldIndex, newIndex);
      dispatch(setAfterIndex({ newAfter: after }));
    }
  };

  /** ISA交換のチェックボックスを表示するか */
  const showConversionAssyCheckbox = () => {
    const { after } = pfrCodeList;
    const selectedRCodeSet = new Set(after.map((item) => item.r.code));

    let countMultipleRequired = 0;
    ConditionAssyRCode.multipleRequired.forEach((rCode) => {
      if (selectedRCodeSet.has(rCode)) {
        countMultipleRequired++;
      }
    });

    return (
      // 必須のRコードが選択されている
      after.some((item) => ConditionAssyRCode.required.includes(item.r.code)) &&
      // 複数選択必須のRコードが2種類以上選択されている
      countMultipleRequired >= 2
    );
  };

  /** 「ISA交換に変換」のチェックボックス押下 */
  const handleConversionAssy = (isChecked: boolean) => {
    setIsConversionAssy(isChecked);
    if (isChecked) {
      setBeforeConversionAfter([...pfrCodeList.after]);
      conversionSelectedR();
    } else {
      restoreSelectedR();
      setBeforeConversionAfter([]);
    }
  };

  /** 「ISA交換に変換」でRコードを置き換える */
  const conversionSelectedR = () => {
    const { main, sub } = confirmedInspection.isaReplacement;
    pfrCodeList.after.forEach((item, index) => {
      if (ConditionAssyRCode.required.includes(item.r.code)) {
        // 主として統合するRコード
        dispatch(
          setSelectedR({
            index: index,
            value: { code: main.code, detail: main.detail },
          }),
        );
      } else if (ConditionAssyRCode.isa.includes(item.r.code)) {
        // 主に紐づくRコード
        dispatch(
          setSelectedR({
            index: index,
            value: { code: sub.code, detail: sub.detail },
          }),
        );
      }
    });
  };

  const restoreSelectedR = () => {
    if (beforeConversionAfter) {
      dispatch(setRestoreSelectedR({ values: beforeConversionAfter }));
    }
  };

  const checkReportedByCustomer = () => {
    setIsReported(!isReported);
    pfrCodeList.after.map((_, rowIndex) => {
      dispatch(
        setReportedByCustomer({
          index: rowIndex,
          value: false,
        }),
      );
    });
  };

  return (
    <React.Fragment>
      <TableContainer component={Paper}>
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={pfrCodeList.after.map((_, id) => id)}
            strategy={verticalListSortingStrategy}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="1%" sx={{ p: '5px' }}></TableCell>
                  <TableCell width="20%" sx={{ minWidth: '180px', p: '5px' }}>
                    {t('LBL_INSPECTION_CATEGORY')}
                  </TableCell>
                  <TableCell width="20%" sx={{ minWidth: '180px', p: '5px' }}>
                    {t('LBL_INSPECTION_SUB_CATEGORY')}
                  </TableCell>
                  <TableCell width="20%" sx={{ minWidth: '180px', p: '5px' }}>
                    {t('LBL_INSPECTION_ITEM')}
                  </TableCell>
                  <TableCell
                    sx={{
                      width: '11%',
                      minWidth: '155px',
                      p: '5px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography>{t('LBL_REPORTED_BY_CUSTOMER')}</Typography>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      justifyContent="center"
                      sx={{ mt: 0.5 }}
                    >
                      <Typography
                        variant="caption"
                        textAlign="right"
                        width="30px"
                      >
                        NO
                      </Typography>
                      <IOSSwitch
                        id={`reported_by_customer_nothing`}
                        sx={{ m: 0, py: 0 }}
                        checked={isReported}
                        onClick={checkReportedByCustomer}
                      />
                      <Typography variant="caption" width="30px">
                        YES
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell width="9%" sx={{ px: '10px' }}>
                    {t('LBL_CD_CODE')}
                  </TableCell>
                  <TableCell
                    width={'20%'}
                    style={{ minWidth: '120px' }}
                    sx={{ pl: '10px' }}
                  >
                    {t('LBL_CD_DETAIL')}
                  </TableCell>
                  <TableCell width={'9%'} sx={{ pl: '10px' }}>
                    {t('LBL_R_CODE')}
                  </TableCell>
                  <TableCell
                    width={'20%'}
                    style={{ minWidth: '200px' }}
                    sx={{ pl: '10px' }}
                  >
                    {t('LBL_R_DETAIL')}
                  </TableCell>
                  <TableCell
                    width={'4%'}
                    style={{ minWidth: '80px', padding: '5px' }}
                  >
                    {t('LBL_P_CODE')}
                  </TableCell>
                  <TableCell
                    width={'20%'}
                    style={{ minWidth: '200px', padding: '5px' }}
                  >
                    {t('LBL_P_DETAIL')}
                  </TableCell>
                  <TableCell
                    width={'4%'}
                    style={{ minWidth: '80px', padding: '5px' }}
                  >
                    {t('LBL_F_CODE')}
                  </TableCell>
                  <TableCell style={{ minWidth: '200px', padding: '5px' }}>
                    {t('LBL_F_DETAIL')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pfrCodeList.after.map((row, rowIndex) => {
                  const isExists =
                    pfrCodeList.before.length === 0 ||
                    pfrCodeList.before.some(
                      (item) => item.itemId === row.itemId,
                    );
                  const { main, sub } = confirmedInspection.isaReplacement;
                  return (
                    <SortableItem
                      key={`after_table_${rowIndex}`}
                      id={rowIndex}
                      isExists={isExists}
                    >
                      <TableCell
                        sx={{ p: '5px', cursor: 'grab', touchAction: 'none' }}
                      >
                        <DragHandleIcon />
                      </TableCell>

                      <TableCell sx={{ p: '5px' }}>
                        <Typography sx={TEXT_OVERFLOW_2LINES}>
                          {row.categoryName}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ p: '5px' }}>
                        <Typography sx={TEXT_OVERFLOW_2LINES}>
                          {row.typeName}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{ p: '5px', whiteSpace: 'pre', maxWidth: '300px' }}
                      >
                        <Typography sx={TEXT_OVERFLOW_1LINE}>
                          {row.itemName.depth0}
                        </Typography>
                        {!isEmptyData(row.itemName.depth1) && (
                          <Typography sx={TEXT_OVERFLOW_1LINE}>
                            {row.itemName.depth1}
                          </Typography>
                        )}
                        {!isEmptyData(row.itemName.depth2) && (
                          <Typography sx={TEXT_OVERFLOW_1LINE}>
                            {row.itemName.depth2}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell
                        style={{ textAlign: 'center', padding: '5px' }}
                      >
                        <Checkbox
                          id={`reported_by_customer_${rowIndex}`}
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={() =>
                            dispatch(
                              setReportedByCustomer({
                                index: rowIndex,
                                value: !row.reportedByCustomer,
                              }),
                            )
                          }
                          checked={row.reportedByCustomer}
                          disabled={!isReported}
                          sx={{
                            bgcolor:
                              (pfrCodeList.after.every(
                                (e) => !e.reportedByCustomer,
                              ) &&
                                isReported) ||
                              row.reportedByCustomer
                                ? 'warning.light'
                                : '',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ p: '5px' }}>
                        <Select
                          id={`cd_code_${rowIndex}`}
                          size="small"
                          sx={{
                            width: '90px',
                            bgcolor: 'warning.light',
                          }}
                          value={row.cd.code}
                          onPointerDown={(e) => e.stopPropagation()}
                          renderValue={(selected) => selected}
                        >
                          {confirmedInspection.cdCodeList.map((cd, i) => (
                            <MenuItem
                              key={`cd_code_${i}`}
                              value={cd.code}
                              onPointerDown={(e) => e.stopPropagation()}
                              onClick={() => {
                                dispatch(
                                  setCdCode({
                                    index: rowIndex,
                                    value: cd,
                                  }),
                                );
                              }}
                            >
                              {cd.code + ' ' + cd.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell sx={{ p: '5px' }}>
                        <TextField
                          size="small"
                          sx={{ width: '1.2' }}
                          value={pfrCodeList.after[rowIndex].cd.name}
                        ></TextField>
                      </TableCell>

                      <TableCell sx={{ p: '5px' }}>
                        {row.rList.length === 1 ||
                        main.code === row.r.code ||
                        sub.code === row.r.code ? (
                          <TextField
                            size="small"
                            sx={{ width: '90px' }}
                            value={row.r.code}
                          />
                        ) : (
                          <Select
                            id={`r_code_${rowIndex}`}
                            size="small"
                            sx={{
                              width: '90px',
                              bgcolor: 'warning.light',
                            }}
                            value={row.r.code}
                            onPointerDown={(e) => e.stopPropagation()}
                            renderValue={(selected) => selected}
                          >
                            {row.rList.map((r, i) => (
                              <MenuItem
                                key={`r_code_${i}`}
                                value={r.code}
                                onPointerDown={(e) => e.stopPropagation()}
                                onClick={() => {
                                  dispatch(
                                    setSelectedR({
                                      index: rowIndex,
                                      value: r,
                                    }),
                                  );
                                }}
                              >
                                {r.code + ' ' + r.detail}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </TableCell>
                      <TableCell sx={{ p: '5px' }}>
                        <Typography sx={TEXT_OVERFLOW_3LINES}>
                          {pfrCodeList.after[rowIndex].r.detail}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ p: '5px' }}>{row.p.code}</TableCell>
                      <TableCell sx={{ p: '5px' }}>
                        <Typography sx={TEXT_OVERFLOW_3LINES}>
                          {row.p.detail}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ p: '5px' }}>{row.f.code}</TableCell>
                      <TableCell sx={{ p: '5px' }}>
                        <Typography sx={TEXT_OVERFLOW_3LINES}>
                          {row.f.detail}
                        </Typography>
                      </TableCell>
                    </SortableItem>
                  );
                })}
              </TableBody>
            </Table>
          </SortableContext>
        </DndContext>
      </TableContainer>
      {isConversionAssy || showConversionAssyCheckbox() ? (
        <Grid size={11.5} textAlign={'right'} marginTop={'10px'}>
          <FormControlLabel
            control={
              <Checkbox
                onChange={(_, isChecked) => handleConversionAssy(isChecked)}
                checked={isConversionAssy}
              ></Checkbox>
            }
            label={t('LBL_CHANGE_ISA_REPLACEMENT')}
          />
        </Grid>
      ) : (
        <></>
      )}
    </React.Fragment>
  );
};

export default AfterTable;
